import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { STR } from '../i18n';
import { ApiError, getRedirectTarget, login } from '../lib/api';
import { type Bounce, bounceMessage, clearBounceReason, peekBounce } from '../lib/bounce';
import { queryKeys } from '../lib/queryKeys';
import { CHANGE_PASSWORD_PATH, landingPath, surfaceById } from '../lib/surfaces';
import { BrandPanel } from '../ui/BrandPanel';
import { Spinner } from '../ui/Primitives';

/**
 * The sign-in surface — the login demo's split panel, which self-labels as the design of
 * record. The brand half renders entirely from `GET /api/branding` (R2).
 *
 * R4 lives here: a failed credential from an anonymous caller gets ONE message, identical
 * for an unknown address, a wrong password and a deactivated account, so this page can never
 * be used to test whether an address exists. The account-inactive message is reachable only
 * from a bounce — i.e. only for a client that already held a live session.
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Passed through to the server untouched. The server owns redirect validation — it knows
  // which applications are registered; the client never validates or second-guesses it, and
  // never renders the raw value, which at this point is unverified input. The only thing that
  // reaches the screen is a name the SERVER chose to give it (see `ContinueToNotice`).
  const redirectParam = searchParams.get('redirect');
  const redirect = redirectParam === null || redirectParam === '' ? undefined : redirectParam;

  // Read during render, consumed on mount. A refresh of /login shows a clean page rather
  // than a stale notice, and the double render development does cannot swallow it.
  const [bounce] = useState<Bounce | null>(() => peekBounce());
  useEffect(() => {
    clearBounceReason();
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);

  const loginMutation = useMutation({
    mutationFn: () => login(redirect === undefined ? { email, password } : { email, password, redirect }),
    onSuccess: async (result) => {
      setFormError(null);

      /*
       * THE SECOND FACTOR OUTRANKS EVERYTHING — the server's own ordering (it issued the
       * '2fa-pending' lane, not a session, so no other surface would admit this person
       * anyway). 'challenge' is an enrolled account proving its factor; 'enroll' is the
       * operator's requirement holding an unenrolled account at set-up. The validated
       * destination rides along as ?redirect= exactly as it does to the wall below, and is
       * re-validated server-side when the challenge or the confirmation submits it back.
       *
       * The pending response carries NO user (nothing about the account is disclosed before
       * the second factor), and the pending lane is refused by GET /api/auth/me — so nothing
       * is primed or invalidated here, and the next screen renders without knowing who it is.
       */
      if (result.twoFactor !== undefined) {
        const target = surfaceById(
          result.twoFactor === 'challenge' ? 'twoFactorChallenge' : 'twoFactorSetup',
        );
        const search =
          result.redirectTo === null
            ? ''
            : `?${new URLSearchParams({ redirect: result.redirectTo }).toString()}`;
        navigate(`${target.path}${search}`, { replace: true });
        return;
      }

      // Past the second-factor branch the response is the complete shape: a user is present.
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });

      /*
       * THE WALL INTERCEPTS FIRST, and it carries the pending destination with it.
       *
       * The server hands back the validated destination AND the walled state in one response,
       * deliberately: honouring the destination before clearing the wall would be a client
       * bug. (Every `authenticated` route 403s `password_change_required` anyway, so the
       * server catches it — but the client must not lean on that.) What travels to the wall
       * is the SERVER's own validated URL, and the wall's screen sends it back to the server
       * to be re-validated from scratch before it is ever used.
       */
      if (result.user.mustChangePassword) {
        const search =
          result.redirectTo === null
            ? ''
            : `?${new URLSearchParams({ redirect: result.redirectTo }).toString()}`;
        navigate(`${CHANGE_PASSWORD_PATH}${search}`, { replace: true });
        return;
      }

      // Non-null is ALWAYS an absolute URL to a registered active application — the server
      // says "leave", or says nothing. There is nothing here to sniff.
      if (result.redirectTo !== null) {
        window.location.assign(result.redirectTo);
        return;
      }

      // Null means "land inside Accounts", and WHICH surface is the client's own call, made
      // from the one route declaration. It is the same surface for everybody — the landing
      // takes no actor, so there is no `isAdmin` branch to write here or to drift from.
      navigate(landingPath(), { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.code === 'rate_limited') {
          setRateLimited(true);
          setFormError(STR.login.rateLimited);
          return;
        }
        setRateLimited(false);
        setFormError(
          error.code === 'invalid_credentials' ? STR.login.badCredentials : error.message,
        );
        return;
      }
      setRateLimited(false);
      setFormError(STR.errors.generic);
    },
  });

  const busy = loginMutation.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (email.trim() === '' || password === '') {
      setRateLimited(false);
      setFormError(STR.login.missingFields);
      return;
    }
    setFormError(null);
    loginMutation.mutate();
  }

  const showNotice = formError === null && bounce !== null;

  return (
    <BrandPanel>
      <div data-view="signin">
        <h1 className="f-title">{STR.login.title}</h1>
        <div className="stack">
          {redirect === undefined ? null : <ContinueToNotice redirect={redirect} />}

          {showNotice && bounce !== null ? (
            <div className={bounce.kind === 'signed-out' ? 'msg call-slate' : 'msg call-orange'}>
              {bounceMessage(bounce, {
                signedOut: STR.login.signedOutNotice,
                accountInactive: STR.login.sessionInactive,
                signedOutElsewhere: STR.login.sessionSignedOutElsewhere,
                passwordChanged: STR.login.sessionPasswordChanged,
                sessionEnded: STR.login.sessionEnded,
                elevationRequired: STR.login.sessionElevationRequired,
                expired: STR.login.sessionExpired,
              })}
            </div>
          ) : null}

          {formError === null ? null : (
            <div className={rateLimited ? 'msg call-orange' : 'msg call-red'} role="alert">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="stack">
              <label className="lbl" htmlFor="login-email">
                <span>{STR.login.emailLabel}</span>
                <input
                  className="field"
                  id="login-email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  placeholder={STR.login.emailPlaceholder}
                  disabled={busy}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (!rateLimited) setFormError(null);
                  }}
                />
              </label>
              <label className="lbl" htmlFor="login-password">
                <span>{STR.login.passwordLabel}</span>
                <input
                  className="field"
                  id="login-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  disabled={busy}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (!rateLimited) setFormError(null);
                  }}
                />
              </label>
              <div className="actions">
                <button className="prim btn btn-main" type="submit" disabled={busy} aria-busy={busy}>
                  {busy ? <Spinner /> : null}
                  <span>{busy ? STR.login.submitting : STR.login.submit}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <p className="aux">{STR.login.aux}</p>
    </BrandPanel>
  );
}

/**
 * "You'll continue to <name> after signing in." — rendered IFF the SERVER named the target.
 *
 * ITS FAILURE MODE IS SILENCE. When the destination is unregistered, inactive, unparseable,
 * or the lookup itself fails (a 429 from the public endpoint's IP limiter included), this
 * renders nothing at all. There is deliberately no generic fallback: telling somebody they
 * will continue where they left off, when the server has already decided they will land on
 * the Accounts root instead, is a claim this app cannot back — and a wrong promise about
 * where a sign-in leads is worse than no promise.
 *
 * It never blocks the form, never shows a spinner over it, and never surfaces an error. It
 * lives in its own component so the lookup's hook is unconditional while the notice itself is
 * conditional, and so a slow answer can never hold up the credentials.
 *
 * Exported for the two-step challenge screen, which is this page one step later and shows
 * the same promise under the same rules — one component, so the two can never drift.
 */
export function ContinueToNotice({ redirect }: { redirect: string }) {
  const target = useQuery({
    queryKey: queryKeys.redirectTarget(redirect),
    queryFn: () => getRedirectTarget(redirect),
    // The answer is stable for as long as this page is open, and a retry storm against a
    // rate-limited public endpoint would only make the silence more likely.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const name = target.data?.application?.name ?? null;
  if (name === null) return null;

  return (
    <span className="pill c-slate">
      {/* The non-breaking space is the demo's: it keeps the application's name and the
          words after it together rather than letting the pill break between them. */}
      {STR.login.continueTo} <b>{name}</b>
      {'\u00a0'}
      {STR.login.continueToSuffix}
    </span>
  );
}
